// btw-front/src/lib/partnerSignupValidation.js
//
// 가입 신청 폼 검증 — 순수 함수로 분리해서 컴포넌트 외부에서 단위 테스트 가능.
// 컴포넌트(PartnerSignup.jsx)는 결과의 errorKey 를 i18n.t() 로 메시지화.

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_RE = /^[\d-]{9,15}$/;
// 비밀번호 정책: 8자 이상 + 영문 1+ + 숫자 1+ + 특수문자 1+
export const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
// 로그인 ID 정책: 영문 소문자로 시작 + 영문 소문자/숫자/언더스코어, 4~30자
// 백엔드 utils/loginId.js 와 동일.
export const LOGIN_ID_RE = /^[a-z][a-z0-9_]{3,29}$/;

export function normalizeLoginId(input) {
  if (typeof input !== "string") return "";
  return input.trim().toLowerCase();
}

/**
 * 국세청 사업자등록번호 체크섬.
 * @param {string} num10 — 하이픈 제거 10자리 숫자 문자열
 * @returns {boolean}
 */
export function isValidBusinessNumber(num10) {
  if (typeof num10 !== "string") return false;
  if (!/^\d{10}$/.test(num10)) return false;
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(num10[i], 10) * w[i];
  sum += Math.floor((parseInt(num10[8], 10) * 5) / 10);
  const check = (10 - (sum % 10)) % 10;
  return check === parseInt(num10[9], 10);
}

/** 하이픈/공백 제거 후 10자리만 추출 */
export function normalizeBusinessNumber(input) {
  if (typeof input !== "string") return "";
  return input.replace(/\D/g, "").slice(0, 10);
}

/**
 * 가입 신청 폼 검증.
 * @param {Object} form — 폼 state
 * @param {string} bnDigits — normalizeBusinessNumber(form.businessNumber)
 * @returns {Object<string, string>} — { fieldKey: errorKey } 형태.
 *   errorKey 는 i18n 의 'partners.signup.errors.<x>' 의 마지막 키 (예: 'required', 'email').
 *   빈 객체면 통과.
 */
export function validateSignupForm(form, bnDigits) {
  const e = {};
  if (!LOGIN_ID_RE.test(normalizeLoginId(form.loginId || ""))) e.loginId = "loginId";

  if (!form.companyName) e.companyName = "required";

  if (bnDigits.length !== 10) e.businessNumber = "businessNumber";
  else if (!isValidBusinessNumber(bnDigits)) e.businessNumber = "businessNumberInvalid";

  if (!form.representativeName) e.representativeName = "required";
  if (!EMAIL_RE.test(form.companyEmail || "")) e.companyEmail = "email";

  if (!form.contactName) e.contactName = "required";
  if (!EMAIL_RE.test(form.contactEmail || "")) e.contactEmail = "email";
  if (!PHONE_RE.test(form.contactPhone || "")) e.contactPhone = "phone";
  if (!PASSWORD_RE.test(form.password || "")) e.password = "password";
  if (form.password !== form.passwordConfirm) e.passwordConfirm = "passwordMismatch";
  if (!form.termsService || !form.termsPrivacy) e.terms = "terms";

  return e;
}

/**
 * 기존 회사 가입 폼 검증 — 회사 정보는 받지 않고 고른 회사(company)만 확인한다.
 * 영업대표 정보(이름·휴대폰·이메일)는 필수 — 승인 시 계정 정보이자 그 계정의 첫 영업대표가 된다.
 * @param {Object} form — 폼 state (신규 회사 가입과 같은 state 를 쓴다)
 * @param {{id: string}|null} company — 회사명 검색으로 고른 회사
 * @returns {Object<string, string>} — validateSignupForm 과 같은 형식
 */
export function validateMemberSignupForm(form, company) {
  const e = {};
  if (!LOGIN_ID_RE.test(normalizeLoginId(form.loginId || ""))) e.loginId = "loginId";
  if (!PASSWORD_RE.test(form.password || "")) e.password = "password";
  if (form.password !== form.passwordConfirm) e.passwordConfirm = "passwordMismatch";
  if (!company || !company.id) e.partnerId = "companyRequired";
  if (!form.contactName) e.contactName = "required";
  if (!EMAIL_RE.test(form.contactEmail || "")) e.contactEmail = "email";
  if (!PHONE_RE.test(form.contactPhone || "")) e.contactPhone = "phone";
  if (!form.termsService || !form.termsPrivacy) e.terms = "terms";
  return e;
}

/**
 * 폼 필드 → i18n 키 매핑 (사용자에게 어떤 필드가 실패했는지 안내용).
 * 호출부에서 t(FIELD_LABELS[k]) 로 메시지화 — en 토스트에서 한국어가 새지 않도록.
 */
export const FIELD_LABELS = {
  loginId: "partners.signup.fields.loginId",
  companyName: "partners.signup.fields.companyName",
  businessNumber: "partners.signup.fields.businessNumber",
  representativeName: "partners.signup.fields.representativeName",
  companyEmail: "partners.signup.fields.companyEmail",
  contactName: "partners.signup.fields.contactName",
  contactEmail: "partners.signup.fields.contactEmail",
  contactPhone: "partners.signup.fields.contactPhone",
  password: "partners.signup.fields.password",
  passwordConfirm: "partners.signup.fields.passwordConfirm",
  terms: "partners.signup.sectionTerms",
  partnerId: "partners.signup.member.companyLabel",
};

// errorKey 우선순위 — 위에서 아래 순서로 사용자에게 안내 (계정이 폼 최상단)
export const FIELD_ORDER = [
  "loginId",
  "password",
  "passwordConfirm",
  "companyName",
  "businessNumber",
  "representativeName",
  "companyEmail",
  "contactName",
  "contactEmail",
  "contactPhone",
  "terms",
];

// 기존 회사 가입 폼의 안내 순서 — 계정 → 회사 선택 → 영업대표 → 약관
export const FIELD_ORDER_MEMBER = [
  "loginId",
  "password",
  "passwordConfirm",
  "partnerId",
  "contactName",
  "contactEmail",
  "contactPhone",
  "terms",
];
